import { Socket } from 'socket.io-client';
import { store } from '../store';
import api from './api';
import { logout as logoutThunk } from '../store/slices/authSlice';
import {
  updateWorkerLocation,
  updateWorkerStatus,
  addAlert,
  addActivity,
  setConnectionStatus,
  updateConnectionStatus,
  resetRealtimeData,
  batchUpdateWorkers,
  setAlertAcknowledgement,
  setAlertResolution
} from '../store/slices/realtimeSlice';
import { RealtimeMessage, Alert, Activity, Location } from '../types';
import { normalizeAlertSeverity } from '../utils/alertSeverity';

const logRealtimeDebug = (...args: unknown[]) => {
  if (import.meta.env.DEV) {
    console.debug(...args);
  }
};

export class WebSocketService {
  private socket: Socket | null = null;
  private nativeWs: WebSocket | null = null;  // For API Gateway WebSocket
  // SSE is the real-time transport on App Runner (which can't do WebSockets).
  private eventSource: EventSource | null = null;
  // Transport-agnostic pub/sub so wsService.on(...) works regardless of whether
  // events arrive over SSE, Socket.IO, or API Gateway WS.
  private listeners = new Map<string, Set<(payload: unknown) => void>>();
  private reconnectAttempts = 0;
  // Full-jitter exponential backoff: delay = random(0, min(cap, base * 2^attempt)).
  // The client NEVER gives up — once the ceiling reaches the cap it keeps
  // retrying forever at jittered-cap intervals, so a backend deploy of any
  // length recovers without a manual page reload, and a fleet of clients
  // can't thunder back in lockstep.
  private static readonly RECONNECT_BASE_MS = 1_000;
  private static readonly RECONNECT_CAP_MS = 60_000;
  // Single-flight guard: a failure can surface through more than one path in
  // the same tick (es.onerror + token-mint catch) — only one retry may be
  // pending at a time or backoff turns into exponential growth.
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private facilityId: string | null = null;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  // App Runner's edge proxy force-closes a long-lived SSE response at ~120s,
  // which surfaces in the browser console as ERR_INCOMPLETE_CHUNKED_ENCODING.
  // We pre-empt it: cleanly close + reopen the stream just BEFORE the cap. A
  // client-initiated close doesn't log a network error, so the console stays
  // clean and the reconnect carries a fresh ws-token + warm-start floor-update.
  // Recycle at 95s + random(0, 15s), recomputed fresh each cycle, so clients
  // that connected together (e.g. right after a deploy) drift apart instead
  // of recycling — and re-hitting the ws-token endpoint — in lockstep.
  private connAgeTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly RECYCLE_BASE_MS = 95_000;
  private static readonly RECYCLE_JITTER_MS = 15_000;
  private subscriptions: Set<string> = new Set();
  private useApiGateway = false;  // Flag for API Gateway WebSocket
  // Guards against re-entrant connect() while a ws-token fetch is in flight
  private connectInFlight = false;
  // Incremented by disconnect() so a ws-token request started by a previous
  // route cannot open a stream after realtime has been intentionally disabled.
  private connectionEpoch = 0;

  constructor() {
    // Bind methods to preserve context
    this.connect = this.connect.bind(this);
    this.disconnect = this.disconnect.bind(this);
    this.handleMessage = this.handleMessage.bind(this);
  }

  /**
   * Connect to the realtime backend.
   *
   * Security fix C6: the long-lived JWT is no longer reachable from JS, so we
   * call GET /api/v1/auth/ws-token first to mint a 30-second `purpose: 'ws'`
   * token, then hand THAT to Socket.IO / API Gateway. The browser sends the
   * httpOnly auth cookie automatically when fetching the ws-token, so the
   * caller doesn't need to provide any credential at all — just the
   * facility ID.
   *
   * `legacyAuthToken` is accepted for backward compatibility with old
   * callers but is intentionally ignored. New callers should pass nothing.
   */
  async connect(facilityId: string, _legacyAuthToken?: string): Promise<void> {
    const switchingFacility = this.facilityId !== null && this.facilityId !== facilityId;

    if (!switchingFacility &&
        this.eventSource &&
        this.eventSource.readyState !== EventSource.CLOSED) {
      return;
    }
    if (!switchingFacility && this.connectInFlight) {
      return;
    }

    if (switchingFacility) {
      this.disconnect();
      store.dispatch(resetRealtimeData());
    }

    this.connectInFlight = true;
    const connectionEpoch = this.connectionEpoch;

    this.facilityId = facilityId;

    store.dispatch(updateConnectionStatus({
      connectionStatus: 'connecting',
      error: undefined,
    }));

    let wsToken: string;
    try {
      wsToken = await api.getWsToken();
      if (!wsToken) {
        throw new Error('Empty ws-token');
      }
    } catch (err) {
      if (connectionEpoch !== this.connectionEpoch) return;
      this.connectInFlight = false;
      console.error('Failed to obtain ws-token:', err);

      // A 401 here means the httpOnly auth cookie is gone or expired. The
      // user *thinks* they're logged in (because of cached profile state)
      // but the server has cut them off — without dispatching logout we'd
      // stay in a permanent reconnect-error loop with the WS error banner.
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        store.dispatch(logoutThunk());
        store.dispatch(updateConnectionStatus({
          connectionStatus: 'error',
          error: 'Session expired',
        }));
        return;
      }

      // Anything else (5xx mid-deploy, network blip, timeout) is retryable —
      // schedule a jittered reconnect instead of leaving the stream dead
      // until a manual page reload.
      store.dispatch(updateConnectionStatus({
        connectionStatus: 'error',
        error: 'Failed to authorize WebSocket',
      }));
      this.handleReconnect();
      return;
    }

    if (connectionEpoch !== this.connectionEpoch) return;

    // SSE transport: App Runner cannot do WebSockets, so real-time push uses a
    // long-lived Server-Sent Events stream. EventSource can't set an
    // Authorization header, so the short-lived ws-token rides in the query
    // string (validated server-side in streamController).
    const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:8081/api/v1';
    const url = `${apiBase}/stream/${encodeURIComponent(facilityId)}?token=${encodeURIComponent(wsToken)}`;

    try {
      const es = new EventSource(url);
      this.eventSource = es;
      this.setupSseHandlers(es);
    } catch (err) {
      console.error('Failed to open SSE stream:', err);
      store.dispatch(updateConnectionStatus({
        connectionStatus: 'error',
        error: 'Failed to open real-time stream',
      }));
      this.handleReconnect();
    } finally {
      if (connectionEpoch === this.connectionEpoch) {
        this.connectInFlight = false;
      }
    }
  }

  /**
   * Wire an EventSource to the store + the internal pub/sub. The built-in
   * handlers keep realtimeSlice updated; emitLocal() re-broadcasts to
   * wsService.on(...) subscribers (e.g. useFloorOverviewData).
   */
  private setupSseHandlers(es: EventSource): void {
    es.addEventListener('connected', () => {
      if (this.eventSource !== es) return;
      this.reconnectAttempts = 0;
      store.dispatch(setConnectionStatus({
        connected: true,
        connectionStatus: 'connected',
        lastConnected: Date.now(),
        error: undefined,
      }));
      // Pre-empt App Runner's ~120s stream cap with a clean recycle.
      this.scheduleConnRecycle();
    });

    es.onerror = () => {
      // Ignore errors from a superseded EventSource.
      if (this.eventSource !== es) return;
      // EventSource's built-in retry reuses the SAME (now-stale) ws-token, so we
      // close it and run our own reconnect that mints a fresh token.
      store.dispatch(setConnectionStatus({
        connected: false,
        connectionStatus: 'disconnected',
        error: 'Real-time stream disconnected',
      }));
      this.closeEventSource();
      this.handleReconnect();
    };

    // Map each named SSE event to its built-in store handler (if any) and
    // always re-broadcast to wsService.on(...) subscribers.
    const wired: Array<[string, ((data: any) => void) | undefined]> = [
      ['worker_update', (d) => this.handleWorkerUpdate(d)],
      ['floor-update', undefined], // consumed via wsService.on('floor-update')
      ['alert', (d) => this.handleAlert(d)],
      ['alert_update', (d) => this.handleAlertUpdate(d)],
      ['activity_detected', (d) => this.handleActivityDetected(d)],
      ['sensor_status', (d) => this.handleSensorStatus(d)],
      ['zone_update', (d) => this.handleZoneUpdate(d)],
      ['batch_update', (d) => this.handleBatchUpdate(d)],
      ['proximity_warning', (d) => this.handleProximityWarning(d)],
      ['safety_light_change', (d) => this.handleSafetyLightChange(d)],
      ['device_status', (d) => this.handleDeviceStatus(d)],
    ];

    for (const [name, builtin] of wired) {
      es.addEventListener(name, (ev: MessageEvent) => {
        if (this.eventSource !== es) return;
        let data: unknown;
        try {
          data = JSON.parse(ev.data);
        } catch {
          return;
        }
        if (builtin) builtin(data);
        this.emitLocal(name, data);
      });
    }
  }

  private emitLocal(eventName: string, payload: unknown): void {
    const set = this.listeners.get(eventName);
    if (!set) return;
    for (const handler of set) {
      try {
        handler(payload);
      } catch (err) {
        console.error(`[ws] listener for "${eventName}" threw`, err);
      }
    }
  }

  /**
   * Schedule a clean stream recycle just before App Runner force-closes it.
   * A client-initiated close + reconnect avoids the ERR_INCOMPLETE_CHUNKED_ENCODING
   * the server-side cut would otherwise log, and is NOT counted as an error
   * (reconnectAttempts is left untouched / reset on the next `connected`).
   */
  private scheduleConnRecycle(): void {
    if (this.connAgeTimer) clearTimeout(this.connAgeTimer);
    this.connAgeTimer = setTimeout(() => {
      if (!this.facilityId) return;
      this.closeEventSource();          // clean close — no console error
      void this.connect(this.facilityId); // fresh token + stream + warm-start
    }, this.computeRecycleDelayMs());
  }

  /** 95s + random(0, 15s), fresh each cycle — keeps the fleet desynchronized. */
  private computeRecycleDelayMs(): number {
    return WebSocketService.RECYCLE_BASE_MS +
      Math.random() * WebSocketService.RECYCLE_JITTER_MS;
  }

  private closeEventSource(): void {
    if (this.connAgeTimer) {
      clearTimeout(this.connAgeTimer);
      this.connAgeTimer = null;
    }
    if (this.eventSource) {
      try {
        this.eventSource.close();
      } catch {
        /* already closed */
      }
      this.eventSource = null;
    }
  }

  private handleMessage(message: RealtimeMessage) {
    switch (message.type) {
      case 'worker_update':
        this.handleWorkerUpdate(message.payload);
        break;
      case 'alert':
        this.handleAlert(message.payload);
        break;
      case 'alert_update':
        this.handleAlertUpdate(message.payload);
        break;
      case 'activity_detected':
        this.handleActivityDetected(message.payload);
        break;
      case 'sensor_status':
        this.handleSensorStatus(message.payload);
        break;
      case 'zone_update':
        this.handleZoneUpdate(message.payload);
        break;
      case 'proximity_warning':
        this.handleProximityWarning(message.payload);
        break;
      case 'safety_light_change':
        this.handleSafetyLightChange(message.payload);
        break;
      case 'device_status':
        this.handleDeviceStatus(message.payload);
        break;
      default:
        // Unknown message type - ignore silently
        break;
    }
  }

  private handleWorkerUpdate(data: any) {
    // Support both new (user_id/userId) and legacy (person_id/employeeId) field names
    const workerId = data.user_id || data.userId || data.person_id || data.employeeId;
    const { location, current_zone, current_activity, ...rest } = data;
    
    // Validate location data - now uses x/y pixel coordinates
    if (location && typeof location.x === 'number' && typeof location.y === 'number') {
      const locationData: Location = {
        x: location.x,
        y: location.y,
        user_id: workerId,
        zone_id: location.zone_id || current_zone,
        floor: location.floor,
        confidence: location.confidence,
        timestamp: location.timestamp || Date.now(),
      };

      store.dispatch(updateWorkerLocation({
        workerId,
        location: locationData,
        current_zone,
        current_activity,
      }));
    }

    // Update worker status if provided
    if (rest.alert_status || rest.sensor_status) {
      store.dispatch(updateWorkerStatus({
        workerId,
        alert_status: rest.alert_status,
        sensor_status: rest.sensor_status,
      }));
    }
  }

  private handleAlert(alert: Alert) {
    const severity = normalizeAlertSeverity(alert.severity);
    if (severity) {
      alert.severity = severity;
    }

    // Ensure alert has required fields
    if (!alert.id) {
      alert.id = `alert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }
    if (!alert.timestamp) {
      alert.timestamp = Date.now();
    }
    if (!alert.site_id) {
      alert.site_id = this.facilityId || '';
    }

    store.dispatch(addAlert(alert));

    // Show browser notification for critical alerts
    if (severity === 'critical' && 'Notification' in window && Notification.permission === 'granted') {
      new Notification('Critical Alert', {
        body: alert.message,
        icon: '/alert-icon.png',
        tag: alert.id,
      });
    }
  }

  /**
   * Applies authoritative acknowledgement and resolution broadcasts. Alert
   * mutations use REST, and the server remains the source of truth for actors
   * and timestamps.
   */
  private handleAlertUpdate(data: unknown): void {
    if (!data || typeof data !== 'object') return;

    const {
      alertId,
      facility_id: eventFacilityId,
      status,
      acknowledged,
      acknowledged_by: acknowledgedBy,
      acknowledged_at: acknowledgedAt,
      resolved,
      resolved_at: resolvedAt,
    } = data as Record<string, unknown>;

    if (
      typeof alertId !== 'string' || !alertId.trim()
      || typeof eventFacilityId !== 'string'
      || eventFacilityId !== this.facilityId
      || acknowledged !== true
      || typeof acknowledgedBy !== 'string' || !acknowledgedBy.trim()
      || typeof acknowledgedAt !== 'string' || !acknowledgedAt.trim()
    ) return;

    const timestamp = Date.parse(acknowledgedAt);
    if (!Number.isFinite(timestamp) || timestamp <= 0) return;

    if (status === 'acknowledged') {
      store.dispatch(setAlertAcknowledgement({
        alertId,
        acknowledged: true,
        acknowledgedBy,
        acknowledgedAt: timestamp,
      }));
      return;
    }

    if (
      status !== 'resolved'
      || resolved !== true
      || typeof resolvedAt !== 'string' || !resolvedAt.trim()
    ) return;

    const resolutionTimestamp = Date.parse(resolvedAt);
    if (!Number.isFinite(resolutionTimestamp) || resolutionTimestamp <= 0) return;

    store.dispatch(setAlertResolution({
      alertId,
      resolved: true,
      resolvedAt: resolutionTimestamp,
      acknowledged: true,
      acknowledgedBy,
      acknowledgedAt: timestamp,
    }));
  }

  private handleActivityDetected(activity: Activity) {
    // Ensure activity has required fields
    if (!activity.id) {
      activity.id = `activity-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }
    if (!activity.start_time) {
      activity.start_time = Date.now();
    }

    store.dispatch(addActivity(activity));
  }

  private handleSensorStatus(data: any) {
    // Support both new (user_id/userId) and legacy (person_id/employeeId) field names
    const workerId = data.user_id || data.userId || data.person_id || data.employeeId;
    const { status } = data;
    
    if (workerId) {
      store.dispatch(updateWorkerStatus({
        workerId,
        sensor_status: status,
      }));
    }
  }

  private handleZoneUpdate(data: any) {
    const { zone_id, workers } = data;
    
    // Update worker locations if provided
    if (workers && Array.isArray(workers)) {
      workers.forEach((worker: any) => {
        const workerId = worker.user_id || worker.userId || worker.person_id || worker.employeeId || worker.id;
        if (workerId && worker.location) {
          store.dispatch(updateWorkerLocation({
            workerId,
            location: worker.location,
            current_zone: zone_id,
          }));
        }
      });
    }
  }

  private handleBatchUpdate(data: any) {
    if (data.workers && Array.isArray(data.workers)) {
      store.dispatch(batchUpdateWorkers(data.workers));
    }
  }

  private handleProximityWarning(data: any) {
    // Normalize to an alert so the existing alert pipeline surfaces it to the UI
    const eventId = data.eventId ?? data.id;
    const workerId = data.workerId ?? data.worker_id;
    const alert: Alert = {
      ...data,
      id: typeof eventId === 'string' && eventId
        ? eventId
        : `proximity-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
      site_id: this.facilityId || data.facilityId || data.facility_id || data.site_id || '',
      worker_id: typeof workerId === 'string' && workerId ? workerId : undefined,
      alert_type: 'proximity_warning',
      severity: normalizeAlertSeverity(data.severity) ?? 'medium',
      message: typeof data.message === 'string' && data.message
        ? data.message
        : 'Proximity warning',
      timestamp: typeof data.timestamp === 'number' && Number.isFinite(data.timestamp)
        ? data.timestamp
        : Date.now(),
      acknowledged: false,
      resolved: false,
    };
    this.handleAlert(alert);
  }

  private handleSafetyLightChange(data: any) {
    // Normalize to an alert so safety light state changes are visible in the alert feed
    const eventId = data.eventId ?? data.id;
    const workerId = data.workerId ?? data.worker_id;
    const alert: Alert = {
      ...data,
      id: typeof eventId === 'string' && eventId
        ? eventId
        : `safety-light-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
      site_id: this.facilityId || data.facilityId || data.facility_id || data.site_id || '',
      worker_id: typeof workerId === 'string' && workerId ? workerId : undefined,
      alert_type: 'safety_light_change',
      severity: normalizeAlertSeverity(data.severity) ?? 'low',
      message: typeof data.message === 'string' && data.message
        ? data.message
        : `Safety light changed to ${data.state ?? 'unknown'}`,
      timestamp: typeof data.timestamp === 'number' && Number.isFinite(data.timestamp)
        ? data.timestamp
        : Date.now(),
      acknowledged: false,
      resolved: false,
    };
    this.handleAlert(alert);
  }

  private handleDeviceStatus(data: any) {
    // Support both new (user_id/userId) and legacy (person_id/employeeId) field names
    const workerId = data.user_id || data.userId || data.person_id || data.employeeId || data.worker_id;
    const deviceId = data.device_id || data.deviceId;
    const { status, battery_level, last_seen, online } = data;

    if (workerId) {
      // Derive sensor_status from the device_status payload
      let sensor_status: 'active' | 'inactive' | 'disconnected' = 'inactive';
      if (status === 'online' || online === true) {
        sensor_status = 'active';
      } else if (status === 'offline' || online === false) {
        sensor_status = 'disconnected';
      }

      store.dispatch(updateWorkerStatus({
        workerId,
        sensor_status,
      }));

      // Log device health details for monitoring
      if (battery_level !== undefined || last_seen !== undefined) {
        logRealtimeDebug(
          `[device_status] Worker ${workerId} device health:`,
          { status: sensor_status, battery_level, last_seen, deviceId }
        );
      }
    }

    // Log for debugging if device_id is present but no worker mapping
    if (deviceId && !workerId) {
      logRealtimeDebug(`[device_status] Received status for device ${deviceId} but no worker mapping`, data);
    }
  }

  /** Full jitter: random(0, min(cap, base * 2^(attempt-1))). */
  private computeReconnectDelayMs(attempt: number): number {
    const ceiling = Math.min(
      WebSocketService.RECONNECT_CAP_MS,
      WebSocketService.RECONNECT_BASE_MS * Math.pow(2, attempt - 1),
    );
    return Math.random() * ceiling;
  }

  private handleReconnect() {
    if (this.reconnectTimer) return; // single-flight — see field comment

    this.reconnectAttempts++;
    const delay = this.computeReconnectDelayMs(this.reconnectAttempts);

    store.dispatch(updateConnectionStatus({
      connectionStatus: 'connecting',
      reconnectAttempts: this.reconnectAttempts,
    }));

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null; // release BEFORE connect so a failure can re-queue
      if (this.facilityId) {
        // Each reconnect mints a fresh ws-token (cookie-authenticated) — see connect().
        void this.connect(this.facilityId);
      }
    }, delay);
  }

  // SSE keeps itself warm via server-sent `: ping` comments (streamController);
  // no client-side heartbeat is needed.

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  subscribe(channels: string[]) {
    // SSE streams are facility-scoped, so subscription is implicit — the server
    // sends every real-time event for the facility. Track the channel names
    // locally for getSubscriptions()/diagnostics.
    channels.forEach(channel => {
      this.subscriptions.add(channel);
    });
  }

  unsubscribe(channels: string[]) {
    if (!this.socket?.connected) {
      return;
    }

    channels.forEach(channel => {
      this.subscriptions.delete(channel);
    });

    this.socket.emit('unsubscribe', {
      facility_id: this.facilityId,
      channels,
    });
  }

  sendMessage(event: string, data: any) {
    if (!this.socket?.connected) {
      return false;
    }

    this.socket.emit(event, {
      ...data,
      facility_id: this.facilityId,
      timestamp: Date.now(),
    });

    return true;
  }

  // Removed: acknowledgeAlert and resolveAlert socket emitters. Alert mutations
  // use REST; authoritative acknowledgement broadcasts update Redux through
  // handleAlertUpdate without emitting mutation actions over Socket.IO.

  requestWorkerDetails(employeeId: string) {
    return this.sendMessage('request_worker_details', {
      person_id: employeeId,
    });
  }

  requestZoneDetails(zoneId: string) {
    return this.sendMessage('request_zone_details', {
      zone_id: zoneId,
    });
  }

  updateWorkerAssignment(employeeId: string, sensorPairId: string) {
    return this.sendMessage('update_worker_assignment', {
      person_id: employeeId,
      sensor_pair_id: sensorPairId,
    });
  }

  disconnect() {
    this.connectionEpoch += 1;
    this.connectInFlight = false;
    this.stopHeartbeat();
    this.closeEventSource();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    if (this.nativeWs) {
      this.nativeWs.close();
      this.nativeWs = null;
    }

    this.subscriptions.clear();
    this.facilityId = null;
    this.reconnectAttempts = 0;
    this.useApiGateway = false;

    store.dispatch(setConnectionStatus({
      connected: false,
      connectionStatus: 'disconnected',
    }));
  }

  isConnected(): boolean {
    if (this.eventSource) {
      return this.eventSource.readyState === EventSource.OPEN;
    }
    if (this.useApiGateway) {
      return this.nativeWs?.readyState === WebSocket.OPEN;
    }
    return this.socket?.connected || false;
  }

  getConnectionStatus(): string {
    if (this.eventSource) {
      switch (this.eventSource.readyState) {
        case EventSource.CONNECTING: return 'connecting';
        case EventSource.OPEN: return 'connected';
        case EventSource.CLOSED: return 'disconnected';
        default: return 'disconnected';
      }
    }
    if (this.useApiGateway) {
      if (!this.nativeWs) return 'disconnected';
      switch (this.nativeWs.readyState) {
        case WebSocket.CONNECTING: return 'connecting';
        case WebSocket.OPEN: return 'connected';
        case WebSocket.CLOSING: return 'disconnecting';
        case WebSocket.CLOSED: return 'disconnected';
        default: return 'disconnected';
      }
    }
    if (!this.socket) return 'disconnected';
    if (this.socket.connected) return 'connected';
    if (this.socket.disconnected) return 'disconnected';
    return 'connecting';
  }

  getFacilityId(): string | null {
    return this.facilityId;
  }

  getSubscriptions(): string[] {
    return Array.from(this.subscriptions);
  }

  /**
   * Register a typed listener on the underlying Socket.IO socket.
   * Returns an unsubscribe function — call it on cleanup.
   * No-ops silently when the socket hasn't been created yet; the caller
   * should re-subscribe after connect() if that matters.
   */
  on<T>(eventName: string, handler: (payload: T) => void): () => void {
    let set = this.listeners.get(eventName);
    if (!set) {
      set = new Set();
      this.listeners.set(eventName, set);
    }
    set.add(handler as (payload: unknown) => void);
    return () => {
      this.listeners.get(eventName)?.delete(handler as (payload: unknown) => void);
    };
  }
}

// Create singleton instance
const wsService = new WebSocketService();

// Export singleton
export default wsService;
export { wsService };

// Export hook for React components
export const useWebSocketService = () => {
  return wsService;
};
